import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { DeleteOutlined, ArrowRightOutlined, CopyOutlined, MenuOutlined, MoreOutlined, EditOutlined } from "@ant-design/icons";
import { Card, Badge, Tooltip, Space, Divider, Dropdown, Menu } from "antd";
import styles from "./ChecklistInListItem.module.css";
import animationStyles from "./animations.module.css";
import ChecklistForm from "./CheckListForm";
import ChecklistStatusIndicator from "./ChecklistStatusIndicator";
import { useSettingsState } from "../../state/settingsState";
import { useEditingChecklistState } from "../../state/editingChecklistState";

const ChecklistInListItem = ({ checklist, index, onMove, onDragEnd, isProcessing = false, onCreateSubchecklist }) => {
  const { 
    checklists, 
    selectedStatus,
    handleDeleteChecklist,
    openCopyModal
  } = useSettingsState();
  
  const { setEditingChecklist, editingChecklist } = useEditingChecklistState();
  const [creatingSubChecklist, setCreatingSubChecklist] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(false);
  
  const subChecklists = checklists.filter(c => c.parent === checklist.id);

  const handleEditChecklist = (checklist) => {
    setEditingChecklist({ ...checklist });
  };

  
  const contextMenuItems = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Редактировать',
      onClick: () => handleEditChecklist(checklist)
    },
    {
      key: 'add-sub',
      icon: <span>+</span>,
      label: 'Создать подчеклист',
      onClick: () => onCreateSubchecklist?.(checklist.id)
    },
    {
      key: 'copy',
      icon: <CopyOutlined />,
      label: 'Копировать',
      onClick: () => openCopyModal(checklist)
    },
    {
      type: 'divider'
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Удалить',
      danger: true,
      onClick: () => handleDeleteChecklist(checklist.id)
    }
  ];

  
  const ref = useRef(null);
  const [{ isDragging }, drag] = useDrag({
    type: "CHECKLIST",
    item: () => ({ 
      id: checklist.id, 
      index: index,
      position: checklist.position, 
      parent: checklist.parent,
      originalPosition: checklist.position
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop() && item.targetIndex !== undefined && item.targetIndex !== item.index && !isProcessing) {
        onDragEnd(item.id, item.targetIndex);
      }
    },
    canDrag: (monitor) => !isProcessing
  });

  const [{ isOver }, drop] = useDrop({
    accept: "CHECKLIST",
    hover: (item, monitor) => {
      if (!ref.current || item.id === checklist.id || item.parent !== checklist.parent || isProcessing) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      item.targetIndex = hoverIndex;
    },
    drop: () => ({ dropped: true }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    canDrop: (item, monitor) => !isProcessing
  });

  drag(drop(ref));

  const isSelected = editingChecklist?.id === checklist.id;

  const itemStyle = {
    opacity: isDragging ? 0.5 : 1,
    cursor: isProcessing ? 'wait' : 'move',
    pointerEvents: isProcessing ? 'none' : 'auto',
  };

  const toggleCollapse = () => setCollapsed(prev => !prev);

  const customFieldsCount = checklist.custom_fields?.length || 0;
  const amoFieldsCount = checklist.fields_ids?.length || 0;
  const syncedFieldsCount = checklist.custom_fields?.filter(cf => 
    cf.type !== 'C' && cf.name 
  ).length || 0;

  const getStatusColor = () => {
    if (customFieldsCount === 0 && amoFieldsCount === 0) return 'default';
    if (customFieldsCount > 0 && amoFieldsCount > 0) return 'success';
    if (customFieldsCount > 0 || amoFieldsCount > 0) return 'warning';
    return 'default';
  };

  return (
    <>
      <Card 
        ref={ref}
        className={`${styles.checklistCard} ${animationStyles.fadeInUp} ${isSelected ? styles.selected : ''} ${isDragging ? styles.isDragging : ''} ${isOver ? styles.isOver : ''} ${isProcessing ? animationStyles.loading : ''}`} 
        style={itemStyle}
        size="small"
        hoverable={!isProcessing}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div className={styles.cardHeader}>
          <Space size="small" className={styles.headerLeft}>
            <MenuOutlined className={styles.dragHandle} />
            {subChecklists.length > 0 && (
              <Tooltip title={collapsed ? "Развернуть" : "Свернуть"}>
                <ArrowRightOutlined
                  className={`${styles.toggleIcon} ${!collapsed ? styles.expandedIcon : ''}`}
                  onClick={toggleCollapse}
                />
              </Tooltip>
            )}
            <span 
              className={styles.checklistName}
              onClick={() => handleEditChecklist(checklist)}
            >
              {checklist.name}
            </span>
          </Space>
          
          <div className={styles.checklistActions}>
            <Tooltip title="Редактировать">
              <span 
                className={`${styles.actionButton} ${animationStyles.microBounce}`} 
                onClick={() => handleEditChecklist(checklist)}
              >
                <EditOutlined />
              </span>
            </Tooltip>
            
            <Dropdown
              menu={{ items: contextMenuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Tooltip title="Дополнительные действия">
                <span className={`${styles.actionButton} ${animationStyles.microBounce}`}>
                  <MoreOutlined />
                </span>
              </Tooltip>
            </Dropdown>
          </div>
        </div>

        <Divider style={{ margin: '8px 0' }} />
        
        <ChecklistStatusIndicator checklist={checklist} compact={false} />
        
        {creatingSubChecklist && creatingSubChecklist.id === checklist.id && (
          <div className={styles.subForm}>
            <ChecklistForm parent={checklist} />
          </div>
        )}
      </Card>
      
      {subChecklists.length > 0 && (
        <div className={`${styles.subChecklistContainer} ${collapsed ? styles.collapsed : styles.expanded}` }>
          {subChecklists.sort((a, b) => a.position - b.position).map(subChecklist => (
            <div key={subChecklist.id} className={styles.subChecklistItem}>
              <div className={styles.subChecklistConnector}></div>
              <ChecklistInListItem
                checklist={subChecklist}
                onMove={onMove}
                onDragEnd={onDragEnd}
                isProcessing={isProcessing}
                onCreateSubchecklist={onCreateSubchecklist}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ChecklistInListItem;

